/**
 * The Add event sheet's requests.
 *
 * Which status a saved game gets is the server's call, not this module's.
 * "Do I have a score" and "is the game over" are different questions
 * (resolve_import_status): treating a checked score box as `final` would
 * freeze a game's partial score forever if ESPN still had it in progress.
 * So the browser sends what the person entered and whether they have a
 * score, and the server asks ESPN and decides.
 */
import { authedFetch } from "./api";
import type { GameStatus, Option } from "./eventsPage";
import { orThrow } from "./http";

/** A watchlist game from the last 60 days that is not logged yet. */
export interface ImportableGame {
  id: number;
  label: string;
  event_date: string;
  league: string;
  home_team: string;
  away_team: string;
  venue_name: string | null;
  venue_city: string | null;
  game_type: string;
  notes: string | null;
  espn_event_id: string | null;
}

export interface AddOptions {
  today: string;
  /** Built-in leagues plus any added by hand before. */
  leagues: string[];
  /** The leagues ESPN can search; anything else is manual entry only. */
  espn_leagues: string[];
  /** For a league added by hand: which sport its logos are looked up under. */
  sports: string[];
  game_types: string[];
  /** Special-event choices; the first is the empty "None". */
  specials: Option[];
  importable: ImportableGame[];
}

export interface EspnGame {
  espn_event_id: string;
  event_date: string;
  home_team: string;
  away_team: string;
  home_score: number | null;
  away_score: number | null;
  status: GameStatus;
  venue_name: string | null;
  venue_city: string | null;
  /** Venue-local start, e.g. "7:00 PM", or null when the zone is unknown. */
  start_time: string | null;
  /** The "Has a final score" default, by the server's has_final_score rule. */
  has_final_score: boolean;
}

export type EventSource = "espn" | "manual" | "upcoming";

export interface NewEvent {
  source: EventSource;
  event_date: string;
  league: string;
  /** Only for a league added by hand, so its logos resolve. */
  sport?: string;
  game_type: string;
  special: string;
  home_team: string;
  away_team: string;
  home_score: number | null;
  away_score: number | null;
  has_score: boolean;
  venue_name: string;
  venue_city: string;
  notes: string;
  espn_event_id?: string | null;
  upcoming_id?: number;
}

export async function fetchAddOptions(): Promise<AddOptions> {
  const res = await authedFetch("/api/events/add-options");
  return (await orThrow(res, "Could not load the Add event form")) as AddOptions;
}

export async function searchEspnGames(league: string, date: string): Promise<EspnGame[]> {
  const qs = new URLSearchParams({ league, date }).toString();
  const res = await authedFetch(`/api/espn/games?${qs}`);
  return (await orThrow(res, "ESPN request failed")) as EspnGame[];
}

/** One game by id, or null when ESPN has no such game. */
export async function fetchEspnEvent(league: string, espnEventId: string): Promise<EspnGame | null> {
  const res = await authedFetch(`/api/espn/event?${new URLSearchParams({ league, espn_event_id: espnEventId })}`);
  if (res.status === 404) return null;
  return (await orThrow(res, "ESPN request failed")) as EspnGame;
}

/** Save a game. `warning` carries a best-effort step that failed after the save (a logo lookup). */
export async function createEvent(e: NewEvent): Promise<{ id: number; warning: string | null }> {
  const res = await authedFetch("/api/events", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(e),
  });
  return (await orThrow(res, "Could not save the event")) as { id: number; warning: string | null };
}

/** "Canadiens @ Bruins (3-2) - final", the line an ESPN result gets in the picker. */
export function espnLabel(g: EspnGame): string {
  const score = g.home_score != null && g.away_score != null ? ` (${g.away_score}-${g.home_score})` : "";
  return `${g.away_team} @ ${g.home_team}${score} · ${g.status.replace("_", " ")}`;
}

/**
 * What the form refuses before sending. The server checks the same things;
 * this is so the person learns before a round trip, not the guard.
 */
export function missingFields(e: Pick<NewEvent, "home_team" | "away_team" | "venue_name" | "venue_city">): string | null {
  if (!e.home_team.trim() || !e.away_team.trim()) return "Home and away team are required.";
  if (!e.venue_name.trim() || !e.venue_city.trim()) return "Venue and city are required.";
  return null;
}
